import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TimezoneSelect from 'react-timezone-select';
import { RiDeleteBinLine } from 'react-icons/ri';

import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import SnackbarNotification, { SNACKBAR_THEME } from '../components/common/SnackbarNotification';
import ConfirmModal from '../components/common/ConfirmModal';
import InstanceScheduler from '../components/common/inventory/InstanceScheduler';

import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import DebugService from '../services/DebugService';
import { useAuth } from '../state/AuthContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import {
  CONST_SITE_LABEL, PLATFORM, STORAGE_KEYS, setScopedItem,
  TAG_BASED_PAGE_LIST, USER_ROLE, ADD_NEW_ACCOUNT,
} from '../components/common/Helper';

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Column definitions ────────────────────────────────────────────
const EC2_COLS = [
  { key: 'id', label: 'Instance ID' },
  { key: 'name', label: 'Name' },
  { key: 'instanceType', label: 'Instance Type' },
  { key: 'region', label: 'Region' },
];

const RDS_INSTANCE_COLS = [
  { key: 'id', label: 'DB Instance ID' },
  { key: 'class', label: 'Class' },
  { key: 'region', label: 'Region' },
];

const RDS_CLUSTER_COLS = [
  { key: 'id', label: 'DB Cluster ID' },
  { key: 'region', label: 'Region' },
];

const ASG_COLS = [
  { key: 'id', label: 'ASG Name' },
  { key: 'DesiredCapacity', label: 'Desired' },
  { key: 'MinSize', label: 'Min' },
  { key: 'MaxSize', label: 'Max' },
  { key: 'region', label: 'Region' },
];

const VM_COLS = [
  { key: 'vmId', label: 'VM ID' },
  { key: 'name', label: 'Name' },
  { key: 'region', label: 'Region' },
];

const DB_COLS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'region', label: 'Region' },
];

const SCHEDULED_COLS = [
  { key: 'serviceType', label: 'Type' },
  { key: 'key', label: 'Tag Key' },
  { key: 'values', label: 'Tag Values', render: r => Array.isArray(r.values) ? r.values.join(', ') : (r.values ?? '—') },
  { key: 'stopTime', label: 'Stop At' },
  { key: 'startTime', label: 'Start At' },
  { key: 'processedStop', label: 'Processed Stop', render: r => r.processedStop ? '✓' : '—' },
  { key: 'processedStart', label: 'Processed Start', render: r => r.processedStart ? '✓' : '—' },
];

// ── ResourceTable sub-component ───────────────────────────────────
function ResourceSection({ title, rows, columns }) {
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <div className="res-section-pill" style={{ marginBottom: 8 }}>{title}</div>
      <Table rows={rows} columns={columns} showToolbar={false} defaultPageSize={100} cardClassName="card" subscriptionGated />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Actions Tab
// ══════════════════════════════════════════════════════════════════
function ActionsTab() {
  const { selectedAccount, selectedPlatform } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();
  const location = useLocation();

  const [filterTagList, setFilterTagList] = useState([]);
  const [filterData, setFilterData] = useState({ tagField: '', tagValue: [] });
  const [currentUserAccess, setCurrentUserAccess] = useState(null);
  const [showResourcesLoading, setShowResourcesLoading] = useState(false);

  // Resource datasets
  const [ec2Data, setEc2Data] = useState([]);
  const [rdsInstanceData, setRdsInstanceData] = useState([]);
  const [rdsClusterData, setRdsClusterData] = useState([]);
  const [asgData, setAsgData] = useState([]);
  const [vmData, setVmData] = useState([]);
  const [mysqlData, setMysqlData] = useState([]);
  const [postgresqlData, setPostgresqlData] = useState([]);
  const [sqlData, setSqlData] = useState([]);
  const [vmssData, setVmssData] = useState([]);
  const hasResources = ec2Data.length > 0 || rdsInstanceData.length > 0 || rdsClusterData.length > 0
    || asgData.length > 0 || vmData.length > 0 || mysqlData.length > 0
    || postgresqlData.length > 0 || sqlData.length > 0 || vmssData.length > 0;

  // Action flow
  const [instanceAction, setInstanceAction] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [actionStatus, setActionStatus] = useState(null);
  const [actionStatusMessage, setActionStatusMessage] = useState(null);

  const fetchTagList = useCallback(() => {
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT) { setFilterTagList([]); setFilterData({ tagField: '', tagValue: [] }); return; }
    const dataRequest = { type: 'ALL_RESOURCES_TAGS', userAccountId: selectedAccount };
    const isAzure = selectedPlatform === PLATFORM.AZURE;
    const endpoint = isAzure ? APIConstants.ENDPOINT_AZURE_FILTER_TAG : APIConstants.ENDPOINT_AWS_FILTER_TAG;
    const call = isAzure
      ? InventoryApiService.getAzureFilterTagList(endpoint, dataRequest)
      : InventoryApiService.getFilterTagList(endpoint, dataRequest);
    call
      .then(res => {
        const list = res?.data ?? [];
        setFilterTagList(list);
        if (list.length > 0) {
          const firstTag = list[0];
          const firstVal = firstTag?.Value?.length ? [firstTag.Value[0]] : [];
          setFilterData({ tagField: firstTag, tagValue: firstVal });
        } else {
          setFilterData({ tagField: '', tagValue: [] });
        }
      })
      .catch(err => DebugService.error('TagBasedAction: tag list error', err));
  }, [selectedAccount, selectedPlatform]);

  useEffect(() => {
    const stored = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (stored) {
      const parsed = JSON.parse(stored);
      setCurrentUserAccess(parsed.roleName);
    }
    onReset();
    fetchTagList();
  }, [location.pathname]);

  useEffect(() => {
    fetchTagList();
  }, [selectedAccount]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, filterTagList);
  }, [filterTagList]);

  const clearResources = () => {
    setEc2Data([]); setRdsInstanceData([]); setRdsClusterData([]);
    setAsgData([]); setVmData([]); setMysqlData([]);
    setPostgresqlData([]); setSqlData([]); setVmssData([]);
  };

  const onShowResources = async () => {
    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      key: filterData.tagField?.Key,
      values: filterData.tagValue,
    };
    if (!dataRequest.key || !dataRequest.values?.length) return;
    try {
      setShowResourcesLoading(true);
      clearResources();
      const response = selectedPlatform === PLATFORM.AZURE
        ? await InventoryApiService.getAzureTagResourcesByTags(dataRequest)
        : await InventoryApiService.getTagResourcesByTags(dataRequest);

      const d = response?.data ?? {};
      if (d.ec2Instances) setEc2Data(d.ec2Instances.map(i => ({ ...i, id: i.instanceId, name: i.instanceName })));
      if (d.rdsDBInstances) setRdsInstanceData(d.rdsDBInstances.map(i => ({ ...i, id: i.DBInstanceIdentifier, class: i.DBInstanceClass })));
      if (d.rdsDBClusters) setRdsClusterData(d.rdsDBClusters.map(i => ({ ...i, id: i.DBClusterIdentifier })));
      if (d.asgInstances) setAsgData(d.asgInstances.map(i => ({ id: i.AutoScalingGroupName, ...i })));
      if (d.vmInstances) setVmData(d.vmInstances.map(i => ({ id: i.vmId, ...i })));
      if (d.mysqlDatabases) setMysqlData(d.mysqlDatabases.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.postgresqlDatabases) setPostgresqlData(d.postgresqlDatabases.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.sqlDatabases) setSqlData(d.sqlDatabases.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.vmssInstances) setVmssData(d.vmssInstances.map((i, idx) => ({ id: idx + 1, ...i })));
    } catch (err) {
      DebugService.error('TagBasedAction: show resources error', err);
    } finally {
      setShowResourcesLoading(false);
    }
  };

  const onTakeActionHandler = (actionCase) => {
    setActionStatus(null);
    setActionStatusMessage(null);
    setInstanceAction(actionCase);
    if (['START_NOW', 'STOP_NOW', 'SCHEDULE_LATER'].includes(actionCase)) {
      setShowConfirmModal(true);
    }
  };

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false);
    if (!isConfirm) { onReset(); return; }
    if (instanceAction === 'START_NOW') instantActionHandler('start');
    else if (instanceAction === 'STOP_NOW') instantActionHandler('stop');
    else setOpenScheduleDialog(true);
  };

  const instantActionHandler = async (status) => {
    const stored = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parsed = JSON.parse(stored);
    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: parsed?.userId ?? '',
      status,
      key: filterData.tagField?.Key,
      values: filterData.tagValue,
    };
    const call = selectedPlatform === PLATFORM.AZURE
      ? InventoryApiService.azureInstantAction
      : InventoryApiService.instantAction;
    try {
      const res = await call(APIConstants.ENDPOINT_INVENTORY_TAG_INSTANT_ACTION, dataRequest);
      if (res?.data?.error) {
        setActionStatus('failed');
        setActionStatusMessage(res.data.error.message ?? 'Something went wrong');
      } else {
        setActionStatus('success');
        setActionStatusMessage(res?.data?.message ?? `Instances ${status.toLowerCase()}ed successfully`);
        onReset();
      }
    } catch (err) {
      DebugService.error('TagBasedAction: instant action error', err);
      // API sometimes times out while the action still succeeds server-side — mirror old behaviour
      setActionStatus('success');
      setActionStatusMessage(`Instance ${status.toLowerCase()}ed successfully. It will take sometime to reflect.`);
      onReset();
      setTimeout(() => { setActionStatus(null); setActionStatusMessage(null); }, 1000);
    }
  };

  const onScheduleLaterSave = async ({ startDate, stopDate, recurringOption, selectedDays, timezone: tz }) => {
    const stored = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parsed = JSON.parse(stored);
    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: parsed?.userId ?? '',
      startInstanceTime: startDate,
      stopInstanceTime: stopDate,
      type: recurringOption,
      key: filterData.tagField?.Key,
      values: filterData.tagValue,
    };
    if (recurringOption === 'WEEKLY') { dataRequest.days = selectedDays; dataRequest.timezone = tz; }
    const call = selectedPlatform === PLATFORM.AZURE
      ? InventoryApiService.scheduleAzureEvents
      : InventoryApiService.scheduleEvents;
    try {
      const res = await call(APIConstants.ENDPOINT_INVENTORY_TAG_SCHEDULE_EVENTS, dataRequest);
      if (res?.error) {
        setActionStatus('failed');
        setActionStatusMessage(res.error.message ?? 'Something went wrong');
      } else {
        setOpenScheduleDialog(false);
        setActionStatus('success');
        setActionStatusMessage(res?.data?.message ?? 'Schedule set successfully');
      }
    } catch (err) {
      DebugService.error('TagBasedAction: schedule later error', err);
      setActionStatus('failed');
      setActionStatusMessage('Error occurred while scheduling later');
    }
  };

  const onReset = () => {
    setActionStatus(null);
    setActionStatusMessage(null);
    setInstanceAction(null);
    if (filterTagList.length > 0) {
      const firstTag = filterTagList[0];
      const firstVal = firstTag?.Value?.length ? [firstTag.Value[0]] : [];
      setFilterData({ tagField: firstTag, tagValue: firstVal });
    } else {
      setFilterData({ tagField: '', tagValue: [] });
    }
    clearResources();
  };

  const tagValues = filterData.tagField ? (filterData.tagField?.Value ?? []) : [];
  const canShowResources = filterData.tagField && filterData.tagField?.Key && filterData.tagValue.length > 0;
  const isReadOnly = currentUserAccess === USER_ROLE.READ_ONLY;

  return (
    <div>
      {/* Filter bar card */}
      <div className="card first" style={{ marginBottom: 20, padding: '16px 20px', width: '100%' }}>
        <div className="filter-bar" style={{ background: 'transparent', border: 'none', padding: 0, gap: 16, alignItems: 'flex-end', margin: 0, width: '100%', display: 'flex' }}>
          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <label>Select Tag</label>
            <select
              value={filterData.tagField ? JSON.stringify(filterData.tagField) : ''}
              onChange={e => {
                const val = e.target.value ? JSON.parse(e.target.value) : '';
                const firstVal = val?.Value?.length ? [val.Value[0]] : [];
                setFilterData({ tagField: val, tagValue: firstVal });
              }}
              style={{ width: '100%', height: 36, padding: '5px 10px', fontSize: 13, lineHeight: '1.4', boxSizing: 'border-box' }}
            >
              <option value="">— Select Tag —</option>
              {filterTagList.map((t, i) => (
                <option key={i} value={JSON.stringify(t)}>{t.Key}</option>
              ))}
            </select>
          </div>

          <div className="field" style={{ flex: 1.5, minWidth: 200 }}>
            <label>Select Tag Value</label>
            <MultiSelect
              options={tagValues}
              value={filterData.tagValue}
              onChange={selected => setFilterData(prev => ({ ...prev, tagValue: selected }))}
              disabled={!filterData.tagField}
              placeholder="Select tag values..."
            />
          </div>

          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <label>Action</label>
            <select
              value="SELECT_ACTION"
              onChange={e => { if (e.target.value !== 'SELECT_ACTION') onTakeActionHandler(e.target.value); }}
              disabled={filterData.tagValue.length === 0 || isReadOnly}
              style={{ width: '100%', height: 36, padding: '5px 10px', fontSize: 13, lineHeight: '1.4', boxSizing: 'border-box' }}
            >
              <option value="SELECT_ACTION">Select Action</option>
              <option value="START_NOW">Start Now</option>
              <option value="STOP_NOW">Stop Now</option>
              <option value="SCHEDULE_LATER">Schedule Shutdown</option>
            </select>
          </div>

          <button
            className="btn primary"
            onClick={onShowResources}
            disabled={!canShowResources || isReadOnly}
            style={{ alignSelf: 'flex-end', height: 36, padding: '0 20px', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            Show Resources
          </button>

          {hasResources && (
            <button className="btn ghost" onClick={onReset} style={{ alignSelf: 'flex-end', height: 36, padding: '0 16px', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {showResourcesLoading && <div className="loading-bar" style={{ marginTop: 16 }}>Loading resources…</div>}

      {/* Resource tables */}
      {!showResourcesLoading && (
        <>
          <ResourceSection title="EC2 Resources" rows={ec2Data} columns={EC2_COLS} />
          <ResourceSection title="RDS DB Instance Resources" rows={rdsInstanceData} columns={RDS_INSTANCE_COLS} />
          <ResourceSection title="RDS DB Cluster Resources" rows={rdsClusterData} columns={RDS_CLUSTER_COLS} />
          <ResourceSection title="ASG Resources" rows={asgData} columns={ASG_COLS} />
          <ResourceSection title="VM Resources" rows={vmData} columns={VM_COLS} />
          <ResourceSection title="MySQL Resources" rows={mysqlData} columns={DB_COLS} />
          <ResourceSection title="PostgreSQL Resources" rows={postgresqlData} columns={DB_COLS} />
          <ResourceSection title="SQL Resources" rows={sqlData} columns={DB_COLS} />
          <ResourceSection title="VMSS Resources" rows={vmssData} columns={DB_COLS} />
        </>
      )}

      {/* Confirm modal */}
      {showConfirmModal && (
        <ConfirmModal
          open
          message={[<div key="m">Are you sure you want to {instanceAction === 'START_NOW' ? 'start' : instanceAction === 'STOP_NOW' ? 'stop' : 'schedule'} all tagged resources?</div>]}
          onActionTaken={onConfirmModalAction}
        />
      )}

      {/* Schedule dialog */}
      {openScheduleDialog && (
        <InstanceScheduler
          open
          handleClose={() => setOpenScheduleDialog(false)}
          onSave={onScheduleLaterSave}
        />
      )}

      {/* Snackbar */}
      {actionStatusMessage && (
        <SnackbarNotification
          initialOpen
          duration={7000}
          message={actionStatusMessage}
          actionButtonName="Ok"
          theme={actionStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
          yPosition="top"
          xPosition="center"
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Scheduled Events Tab
// ══════════════════════════════════════════════════════════════════
function ScheduledEventsTab() {
  const { selectedAccount, selectedPlatform } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentUserAccess, setCurrentUserAccess] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarCase, setSnackbarCase] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const processData = useCallback((rows) => rows.map(d => {
    const fmt = (t) => {
      try {
        const dt = selectedTimezone?.value
          ? new Date(t).toLocaleString('en-US', { timeZone: selectedTimezone.value })
          : t;
        return format(dt, 'dd-MM-yyyy HH:mm:ss');
      } catch { return t ?? '—'; }
    };
    return { ...d, startTime: d.startTime ? fmt(d.startTime) : '—', stopTime: d.stopTime ? fmt(d.stopTime) : '—' };
  }), [selectedTimezone]);

  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setData([]);
    try {
      const call = selectedPlatform === PLATFORM.AZURE
        ? InventoryApiService.getScheduledAzureEvents
        : InventoryApiService.getScheduledEvents;
      const res = await call(selectedAccount, APIConstants.ENDPOINT_INVENTORY_TAG_SCHEDULED_EVENTS_LIST);
      const list = res?.data ?? [];
      setRawData(list);
      setData(processData(list));
    } catch (err) {
      DebugService.error('TagBasedAction: scheduled events fetch error', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccount, selectedPlatform, processData]);

  useEffect(() => {
    const stored = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (stored) setCurrentUserAccess(JSON.parse(stored).roleName);
    fetchData();
  }, [location.pathname, selectedAccount]);

  useEffect(() => {
    setData(processData(rawData));
  }, [selectedTimezone]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, rawData);
  }, [rawData]);

  const onDeleteClick = () => {
    if (!selectedRows.length) return;
    setIsLoading(true);
    const dataRequest = { userAccountId: selectedAccount ?? '', ids: selectedRows };
    const call = selectedPlatform === PLATFORM.AZURE
      ? InventoryApiService.deleteScheduledAzureEvents
      : InventoryApiService.deleteScheduledEvents;
    call(APIConstants.ENDPOINT_INVENTORY_TAG_DELETE_SCHEDULED_EVENTS, dataRequest)
      .then(res => {
        if (res?.data) {
          setSnackbarCase('success');
          setSnackbarMessage('Scheduled events deleted successfully');
          setTimeout(fetchData, 1000);
        } else if (res?.error) {
          setSnackbarCase('failed');
          setSnackbarMessage(res.error?.message ?? 'Delete failed');
        }
        setIsLoading(false);
      })
      .catch(err => {
        DebugService.error('TagBasedAction: delete scheduled events error', err);
        setSnackbarCase('failed');
        setSnackbarMessage('Delete failed');
        setIsLoading(false);
      });
  };

  const scheduledColsWithCheckbox = [
    {
      key: '_check',
      label: <input type="checkbox" onChange={e => setSelectedRows(e.target.checked ? data.map(r => r.id) : [])} checked={selectedRows.length === data.length && data.length > 0} />,
      render: (row) => (
        <input type="checkbox" checked={selectedRows.includes(row.id)} onChange={e => {
          setSelectedRows(prev => e.target.checked ? [...prev, row.id] : prev.filter(id => id !== row.id));
        }} />
      ),
    },
    ...SCHEDULED_COLS,
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 240 }}>
          <TimezoneSelect value={selectedTimezone} onChange={setSelectedTimezone} />
        </div>
        <button
          className="btn ghost"
          style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', borderColor: '#ef4444' }}
          disabled={!selectedRows.length || currentUserAccess === USER_ROLE.READ_ONLY}
          onClick={() => setShowConfirmModal(true)}
        >
          <RiDeleteBinLine /> Delete
        </button>
      </div>

      <Table
        rows={data}
        columns={scheduledColsWithCheckbox}
        loading={isLoading}
        defaultPageSize={25}
        exportFilename="tag_scheduled_events"
        subscriptionGated
      />

      {showConfirmModal && (
        <ConfirmModal
          open
          message={[<div key="m">Are you sure you want to delete the selected scheduled events?</div>]}
          onActionTaken={(ok) => { setShowConfirmModal(false); if (ok) onDeleteClick(); }}
        />
      )}

      {snackbarMessage && (
        <SnackbarNotification
          initialOpen
          duration={7000}
          message={snackbarMessage}
          actionButtonName="Ok"
          theme={snackbarCase === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
          yPosition="top"
          xPosition="center"
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Untagged List Tab
// ══════════════════════════════════════════════════════════════════
function UntaggedListTab() {
  const { selectedAccount, selectedPlatform } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [ec2Data, setEc2Data] = useState([]);
  const [rdsData, setRdsData] = useState([]);
  const [asgData, setAsgData] = useState([]);
  const [vmData, setVmData] = useState([]);
  const [mysqlData, setMysqlData] = useState([]);
  const [postgresqlData, setPostgresqlData] = useState([]);
  const [sqlData, setSqlData] = useState([]);
  const [vmssData, setVmssData] = useState([]);

  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setEc2Data([]); setRdsData([]); setAsgData([]); setVmData([]);
    setMysqlData([]); setPostgresqlData([]); setSqlData([]); setVmssData([]);
    try {
      const call = selectedPlatform === PLATFORM.AZURE
        ? InventoryApiService.getUntaggedAzureInventory
        : InventoryApiService.getUntaggedInventory;
      const res = await call(selectedAccount);
      const d = res?.data ?? {};
      if (d.ec2?.instances) {
        setEc2Data(d.ec2.instances.map(i => ({
          ...i, id: i.InstanceId,
          name: i.Tags?.find(t => t.Key === 'Name')?.Value ?? '—',
          instanceState: i.State?.Name,
          instanceType: i.InstanceType,
          region: i.Region,
        })));
      }
      if (d.rds?.instances) {
        setRdsData(d.rds.instances.map(i => ({
          ...i,
          DBInstanceIdentifierList: (i.DBClusterMembers ?? []).map(m => m.DBInstanceIdentifier).join(', '),
        })));
      }
      if (d.asg?.instances) setAsgData(d.asg.instances.map(i => ({ id: i.AutoScalingGroupName, ...i })));
      if (d.vm) setVmData(d.vm);
      if (d.mysql) setMysqlData(d.mysql.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.postgresql) setPostgresqlData(d.postgresql.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.sql) setSqlData(d.sql.map((i, idx) => ({ id: idx + 1, ...i })));
      if (d.vmss) setVmssData(d.vmss.map((i, idx) => ({ id: idx + 1, ...i })));
    } catch (err) {
      DebugService.error('TagBasedAction: untagged list error', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccount, selectedPlatform]);

  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) fetchData(); }, [selectedAccount]);

  const UNTAGGED_EC2_COLS = [
    { key: 'id', label: 'Instance ID' },
    { key: 'name', label: 'Name' },
    { key: 'instanceState', label: 'State' },
    { key: 'instanceType', label: 'Type' },
    { key: 'region', label: 'Region' },
  ];
  const UNTAGGED_RDS_COLS = [
    { key: 'DBInstanceIdentifierList', label: 'DB Identifiers' },
    { key: 'region', label: 'Region' },
  ];

  return (
    <div>
      {isLoading && <div className="loading-bar" style={{ marginBottom: 16 }}>Loading untagged resources…</div>}
      {!isLoading && (
        <>
          <ResourceSection title="EC2 Resources" rows={ec2Data} columns={UNTAGGED_EC2_COLS} />
          <ResourceSection title="RDS Resources" rows={rdsData} columns={UNTAGGED_RDS_COLS} />
          <ResourceSection title="ASG Resources" rows={asgData} columns={ASG_COLS} />
          <ResourceSection title="VM Resources" rows={vmData} columns={VM_COLS} />
          <ResourceSection title="MySQL Resources" rows={mysqlData} columns={DB_COLS} />
          <ResourceSection title="PostgreSQL Resources" rows={postgresqlData} columns={DB_COLS} />
          <ResourceSection title="SQL Resources" rows={sqlData} columns={DB_COLS} />
          <ResourceSection title="VMSS Resources" rows={vmssData} columns={DB_COLS} />
          {!ec2Data.length && !rdsData.length && !asgData.length && !vmData.length
            && !mysqlData.length && !postgresqlData.length && !sqlData.length && !vmssData.length && (
            <div style={{ color: 'var(--muted)', padding: '32px', textAlign: 'center' }}>
              No untagged resources found for the selected account.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Main Page Component
// ══════════════════════════════════════════════════════════════════
const TABS = [
  { key: TAG_BASED_PAGE_LIST.ACTIONS, label: 'Actions' },
  { key: TAG_BASED_PAGE_LIST.SCHEDULED, label: 'Scheduled Events' },
  { key: TAG_BASED_PAGE_LIST.UNTAGGED_LIST, label: 'Untagged List' },
];

export default function TagBasedAction() {
  const [activeTab, setActiveTab] = useState(TAG_BASED_PAGE_LIST.ACTIONS);
  const location = useLocation();

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Tag Start/Stop`;
  }, [location.pathname]);

  return (
    <>
      <div className="breadcrumb">
        Inventory <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Tag Start / Stop</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>Tag Start / Stop</h1>
          <div className="meta">Start, stop, or schedule cloud resources by tag — across EC2, RDS, ASG and Azure resources</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-row" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === TAG_BASED_PAGE_LIST.ACTIONS && <ActionsTab />}
      {activeTab === TAG_BASED_PAGE_LIST.SCHEDULED && <ScheduledEventsTab />}
      {activeTab === TAG_BASED_PAGE_LIST.UNTAGGED_LIST && <UntaggedListTab />}
    </>
  );
}
